import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function CustomOrder() {
  const [params] = useSearchParams();
  const productSlug = params.get("product");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", project_type: "chopping_board",
    dimensions: "", wood_preference: "", details: "", budget: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!productSlug) return;
    api.get(`/products/${productSlug}`).then((r) => {
      const p = r.data;
      const projType = p.category === "furniture" ? "other" : p.category;
      setForm((f) => ({
        ...f,
        project_type: projType,
        details: `Enquiry about "${p.name}" (${p.category}${p.subcategory ? ` · ${p.subcategory}` : ""})\n\nPrice listed: $${p.price}\n\nPlease let me know:\n- `,
      }));
    }).catch(() => {});
  }, [productSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/custom-orders", form);
      setDone(true);
      toast.success("Request received. David will be in touch within 48 hours.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="custom-order-success">
        <p className="smallcaps mb-4">Thank You</p>
        <h1 className="font-display text-5xl text-[#212121] mb-6">Your request is in.</h1>
        <p className="text-[#757575] max-w-xl mx-auto">
          David reads every commission request personally and will reply within 48 hours with questions, a quote, and a timeline.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="custom-order-page" className="max-w-3xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <p className="smallcaps mb-3">Commission</p>
      <h1 className="font-display text-5xl sm:text-6xl text-[#212121] mb-6">Custom Piece Request</h1>
      <p className="text-[#757575] mb-12 leading-relaxed max-w-xl">
        Have something specific in mind? A wedding gift, a particular wood, a shape only you would love?
        Tell David about it. Most commissions take 4–8 weeks.
      </p>

      <form onSubmit={submit} className="space-y-6" data-testid="custom-order-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="smallcaps block mb-2">Your Name</label>
            <input data-testid="co-name" required className="input-light" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className="smallcaps block mb-2">Email</label>
            <input data-testid="co-email" type="email" required className="input-light" value={form.email} onChange={set("email")} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="smallcaps block mb-2">Phone (optional)</label>
            <input data-testid="co-phone" className="input-light" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className="smallcaps block mb-2">Project Type</label>
            <select data-testid="co-type" className="input-light" value={form.project_type} onChange={set("project_type")}>
              <option value="chopping_board">Chopping Board</option>
              <option value="resin_platter">Resin Platter</option>
              <option value="furniture">Furniture</option>
              <option value="other">Something Else</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="smallcaps block mb-2">Dimensions</label>
            <input data-testid="co-dim" className="input-light" placeholder='e.g. 18" x 12"' value={form.dimensions} onChange={set("dimensions")} />
          </div>
          <div>
            <label className="smallcaps block mb-2">Wood / Color Preference</label>
            <input data-testid="co-wood" className="input-light" placeholder="e.g. walnut, blue resin" value={form.wood_preference} onChange={set("wood_preference")} />
          </div>
        </div>
        <div>
          <label className="smallcaps block mb-2">Project Details</label>
          <textarea data-testid="co-details" required rows={6} className="input-light" placeholder="Tell us about the piece you have in mind…" value={form.details} onChange={set("details")} />
        </div>
        <div>
          <label className="smallcaps block mb-2">Budget (optional)</label>
          <input data-testid="co-budget" className="input-light" placeholder="e.g. $200-300" value={form.budget} onChange={set("budget")} />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="co-submit">
          {submitting ? "Sending…" : "Send Request"}
        </button>
      </form>
    </div>
  );
}
